import { Component } from '@verndale/core';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import { focusableElements } from '../helpers/helpers';
import figure from './figure';
import { ExtendedWindowType } from '../coveo/engine';

class VideoModal extends Component {
  private focusTrap: FocusTrap;
  private focusableModalElements: NodeList | undefined;
  private focusableElementsString: string | undefined;
  private lastFocusedElement: HTMLElement | null | undefined;
  private videoFigure: figure | undefined;

  constructor(el: HTMLElement) {
    super(el);
    this.focusTrap = createFocusTrap(el) || '';
    this.videoFigure = new figure(this.dom.$figureContainer as HTMLElement);
  }

  setupDefaults() {
    this.dom = {
      $modal: this.el,
      $closeButton: this.el.querySelector<HTMLElement>(
        '.video-modal__close-button'
      ),
      $backdrop: this.el.querySelector<HTMLElement>('.video-modal__backdrop'),
      $content: this.el.querySelector<HTMLElement>('.video-modal__content'),
      $figure: this.el.querySelector<HTMLElement>(
        '.video-modal__content figure'
      ),
      $modalTitle: this.el.querySelector<HTMLElement>('.video-modal__title'),
      $modalSummary: this.el.querySelector<HTMLElement>(
        '.video-modal__summary'
      ),
      $figureContainer:
        this.el.querySelector<HTMLElement>('.figure__container'),
      $captionPicture: this.el.querySelector<HTMLElement>(
        '.figure__container .figure__media picture'
      )
    };

    this.focusTrap = createFocusTrap(this.el) || '';
    this.focusableElementsString = focusableElements.join(', ');
    this.focusableModalElements = (this.el as HTMLElement).querySelectorAll(
      this.focusableElementsString
    );
  }

  addListeners() {
    (this.dom.$backdrop as HTMLElement).addEventListener(
      'click',
      this.handleBackdropClick.bind(this)
    );
    (this.dom.$modal as HTMLElement).addEventListener(
      'keydown',
      this.handleKeydown.bind(this)
    );
    (this.dom.$closeButton as HTMLElement).addEventListener(
      'click',
      this.closeModal.bind(this)
    );
    window.addEventListener('click', this.openModal.bind(this));
  }

  handleBackdropClick(e: Event) {
    if (e.target === this.dom.$backdrop) {
      this.closeModal();
    }
  }

  handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeModal();
    }
  }

  openModal(e: Event) {
    const trigger = e.target as HTMLElement;

    if (trigger?.getAttribute('data-trigger-modal') === this.el.id) {
      this.setModalData(trigger);
      this.lastFocusedElement = document.activeElement as HTMLElement;
      this.focusableModalElements?.forEach(element =>
        (element as HTMLElement).setAttribute('tabindex', '1')
      );

      (this.dom.$modal as HTMLElement).setAttribute('aria-hidden', 'false');
      this.focusTrap.activate();

      if (this.focusableModalElements?.length) {
        if (this.focusableModalElements[0] !== this.dom.$closeButton)
          (this.focusableModalElements[0] as HTMLElement).focus();
      }

      const global = window as ExtendedWindowType;

      global.dataLayer?.push({
        event: 'modal_view',
        event_category: 'modal overlay',
        event_action: 'view',
        event_attributes: {
          modalName: 'video'
        }
      });
    }
  }

  setModalData(trigger: HTMLElement | null) {
    const videoId = trigger?.getAttribute('data-video-id');
    const imageSrc = trigger?.getAttribute('data-image-src');
    const imageSrcset = trigger?.getAttribute('data-image-srcset');
    const imageHeight = trigger?.getAttribute('data-image-height');
    const imageWidth = trigger?.getAttribute('data-image-width');
    const imageDescription = trigger?.getAttribute('data-image-description');
    const imageSrcsetArray = imageSrcset?.split(',');
    const videoTitle = trigger?.getAttribute('data-video-title');
    const videoSummary = trigger?.getAttribute('data-video-summary');
    const videoFigure = this.dom.$figure as HTMLElement;
    const videoPlayerPlay = videoFigure?.dataset.videoPlayerPlay;
    const videoPlayerEnd = videoFigure?.dataset.videoPlayerEnd;
    const videoPlayerProgress = videoFigure?.dataset.videoPlayerProgress;

    if (videoPlayerPlay) {
      videoFigure.dataset.videoPlayerPlay = videoPlayerPlay.replace(
        'videoNamePlaceholder',
        videoTitle as string
      );
    }

    if (videoPlayerEnd) {
      videoFigure.dataset.videoPlayerEnd = videoPlayerEnd.replace(
        'videoNamePlaceholder',
        videoTitle as string
      );
    }

    if (videoPlayerProgress) {
      videoFigure.dataset.videoPlayerProgress = videoPlayerProgress.replace(
        'videoNamePlaceholder',
        videoTitle as string
      );
    }

    let pictureContent = '';
    this.videoFigure?.handleReplaceId(videoId as string, true);
    (this.dom.$modalTitle as HTMLElement).innerHTML = videoTitle as string;
    (this.dom.$modalSummary as HTMLElement).innerHTML = videoSummary as string;

    imageSrcsetArray?.forEach(source => {
      if (source.length > 0) {
        const sourceArray = source.split(' : ');
        pictureContent += `<source media="(max-width: ${sourceArray[0]})" srcset="${sourceArray[1]}">`;
      }
    });
    pictureContent += `<img src="${imageSrc}" alt="${imageDescription}" height="${imageHeight}" width="${imageWidth}">`;
    (this.dom.$captionPicture as HTMLElement).innerHTML = pictureContent;
  }

  closeModal() {
    this.focusTrap.deactivate();
    (this.dom.$modal as HTMLElement).setAttribute('aria-hidden', 'true');
    if (this.lastFocusedElement) this.lastFocusedElement.focus();
    this.videoFigure?.handleExternalVideoPause();

    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event: 'modal_close',
      event_category: 'modal overlay',
      event_action: 'close',
      event_attributes: {
        modalName: 'video'
      }
    });
  }
}

export default VideoModal;
